import { useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import fetchEsimPromoCodesAction from 'actions/loyalty/fetchEsimPromoCodes'
import { fetchOrder } from 'actions/OrderActions'
import { UPDATE_LOYALTY_ESIM_CODE } from 'actions/actionConstants'
import pollOrderService from 'lib/polling/pollOrderService'
import { getOrderById } from 'api/orderLookup'
import { hasUnsettledOrderLineItems, isOrderLineItemsSettled } from 'loyalty/lib/esims/esimOrderUtils'
import { buildEsimPromoCodesView, EMPTY_ESIM_PROMO_CODES_VIEW, type EsimPromoCodesView } from 'loyalty/lib/esims/esimPromoCodesView'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'

interface UseOrderEsimPromoCodesOptions {
  enabled?: boolean
}

const PENDING_ORDER_POLL_WAIT_MS = 30_000
const PENDING_ORDER_POLL_MAX_MS = 2 * 60 * 1_000

/**
 * Single-order eSIM promo card: details API + post-checkout poll until line items settle.
 *
 * When the modal opens before booking confirmation, polls `getOrderById` until every line item
 * is `completed` or `cancelled`, then fetches the benefit/code (or `not_found` if ineligible).
 */
function useOrderEsimPromoCodes(orderId: string, options: UseOrderEsimPromoCodesOptions = {}): EsimPromoCodesView {
  const { enabled = true } = options
  const dispatch = useAppDispatch()
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const order = useAppSelector(state => state.orders.orders[orderId])
  const esimState = useAppSelector(state => state.loyalty.esimPromoCodes)
  const esimPhaseForOrder = esimState.codesByOrderId[orderId]?.phase
  const pollingOrderIdRef = useRef<string | null>(null)

  const visibleCodes = useMemo(() => {
    const entry = esimState.codesByOrderId[orderId]
    // Include booking_pending so post-checkout placeholder shows while line items settle.
    return entry && entry.phase !== 'not_found' ? [entry] : []
  }, [esimState.codesByOrderId, orderId])

  const isOrderEligible = useMemo(
    // consider booking_pending not eligible so that polling continues
    () => visibleCodes.some(code => code.orderId === orderId && code.phase !== 'booking_pending'),
    [visibleCodes, orderId],
  )

  const isBookingPending = useMemo(() => {
    if (isOrderEligible) return false
    if (!order) return false
    return hasUnsettledOrderLineItems(order)
  }, [isOrderEligible, order])

  // 1. Load order
  useEffect(() => {
    if (!enabled) return
    dispatch(fetchOrder(orderId))
  }, [dispatch, orderId, enabled])

  // 2. Load eSIM benefit once the order and loyalty account region are available (skip while line items are still settling)
  useEffect(() => {
    if (!enabled || !accountRegion || !order?.customerId || isBookingPending) return
    dispatch(fetchEsimPromoCodesAction(orderId))
  }, [dispatch, orderId, accountRegion, order?.customerId, isBookingPending, enabled])

  // 3. Post-checkout: poll until line items settle, then re-fetch order + benefit
  useEffect(() => {
    if (!enabled || !isBookingPending || pollingOrderIdRef.current === orderId) return
    dispatch({
      type: UPDATE_LOYALTY_ESIM_CODE,
      orderId,
      update: {
        orderId,
        destination: '',
        dates: '',
        phase: 'booking_pending',
      },
    })

    pollingOrderIdRef.current = orderId
    let cancelled = false

    const releasePollingLock = () => {
      if (pollingOrderIdRef.current === orderId) pollingOrderIdRef.current = null
    }
    const dispatchIfActive = (action: () => void) => {
      if (!cancelled) action()
    }

    pollOrderService({
      validateFunction: (polledOrder: App.Order) => isOrderLineItemsSettled(polledOrder) ? polledOrder : undefined,
      failedStatus: 'failed',
      apiCall: () => getOrderById(orderId),
      maxTime: PENDING_ORDER_POLL_MAX_MS,
      waitTime: PENDING_ORDER_POLL_WAIT_MS,
    }).then(() => {
      dispatchIfActive(() => {
        dispatch(fetchOrder(orderId, { force: true }))
        dispatch(fetchEsimPromoCodesAction(orderId))
      })
    }).catch(() => {
      dispatchIfActive(() => {
        dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId, update: { phase: 'error' } })
      })
    }).finally(releasePollingLock)

    return () => {
      cancelled = true
      releasePollingLock()
    }
  }, [dispatch, orderId, isBookingPending, enabled])

  // 4. Clear stale booking_pending once the order is settled or already has a visible benefit
  useEffect(() => {
    if (!enabled || isBookingPending || isOrderEligible) return
    if (esimPhaseForOrder === 'booking_pending') {
      dispatch({
        type: UPDATE_LOYALTY_ESIM_CODE,
        orderId,
        update: { phase: 'not_found' },
      })
    }
  }, [dispatch, orderId, isBookingPending, isOrderEligible, esimPhaseForOrder, enabled])

  const orderBenefitLoaded = !!esimState.codesByOrderId[orderId]?.phase

  return useMemo(
    () => {
      if (!enabled) return EMPTY_ESIM_PROMO_CODES_VIEW
      return buildEsimPromoCodesView(visibleCodes, {
        orderId,
        codesByOrderId: esimState.codesByOrderId,
        benefitListLoaded: orderBenefitLoaded,
      })
    },
    [enabled, visibleCodes, orderId, esimState.codesByOrderId, orderBenefitLoaded],
  )
}

export default useOrderEsimPromoCodes
